import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/config";
import { Button } from "@/components/ui/button";
import { 
  Phone, AlertTriangle, Heart, Shield, ArrowLeft,
  ExternalLink, MapPin, Clock, Navigation, Loader2
} from "lucide-react";

// Regional emergency numbers database
const REGIONAL_VETS = {
  "Lombardia": [
    { name: "Guardia Veterinaria Milano", phone: "02-88463333", hours: "24/7" },
    { name: "Clinica Veterinaria Città Studi", phone: "02-26681511", hours: "24/7" },
  ],
  "Lazio": [
    { name: "Guardia Veterinaria Roma", phone: "06-67109200", hours: "24/7" },
    { name: "Clinica Veterinaria Roma Sud", phone: "06-5036450", hours: "24/7" },
  ],
  "Piemonte": [
    { name: "Guardia Veterinaria Torino", phone: "011-4424700", hours: "24/7" },
  ],
  "Veneto": [
    { name: "Guardia Veterinaria Padova", phone: "049-8217936", hours: "24/7" },
  ],
  "Emilia-Romagna": [
    { name: "Guardia Veterinaria Bologna", phone: "051-6320840", hours: "24/7" },
  ],
  "Toscana": [
    { name: "Guardia Veterinaria Firenze", phone: "055-2758333", hours: "24/7" },
  ],
  "Campania": [
    { name: "Guardia Veterinaria Napoli", phone: "081-7962111", hours: "24/7" },
  ],
  "Sicilia": [
    { name: "Guardia Veterinaria Palermo", phone: "091-7404111", hours: "24/7" },
  ],
  "default": [
    { name: "Numero Verde ENPA", phone: "800-253608", hours: "24/7", tollFree: true },
  ]
};

export default function Emergency() {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [location, setLocation] = useState(null);
  const [region, setRegion] = useState(null);
  const [localVets, setLocalVets] = useState([]);
  const [geoLoading, setGeoLoading] = useState(true);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    fetchContacts();
    getLocation();
  }, []);

  const fetchContacts = async () => {
    try {
      const response = await fetch(`${API}/emergency/contacts`);
      if (response.ok) {
        const data = await response.json();
        setContacts(data.contacts);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getLocation = () => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          setLocation({ lat: latitude, lng: longitude });
          
          // Try to get region from coordinates (reverse geocoding)
          try {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`
            );
            if (response.ok) {
              const data = await response.json();
              const regionName = data.address?.state || data.address?.region;
              setRegion(regionName);
              
              // Get local emergency numbers
              const vets = REGIONAL_VETS[regionName] || REGIONAL_VETS["default"];
              setLocalVets(vets);
            }
          } catch (e) {
            console.error("Reverse geocoding error:", e);
            setLocalVets(REGIONAL_VETS["default"]);
          }
          setGeoLoading(false);
        },
        (error) => {
          console.error("Geolocation error:", error);
          setLocalVets(REGIONAL_VETS["default"]);
          setGeoLoading(false);
        },
        { timeout: 10000, maximumAge: 300000 }
      );
    } else {
      setLocalVets(REGIONAL_VETS["default"]);
      setGeoLoading(false);
    }
  };

  // Emergency contacts with more details
  const emergencyCards = [
    {
      id: "enpa",
      name: "ENPA",
      fullName: "Ente Nazionale Protezione Animali",
      phone: "06-3242873",
      description: "Emergenze animali, soccorso fauna selvatica, segnalazioni maltrattamenti",
      color: "from-red-500 to-red-600",
      icon: Heart,
      priority: true
    },
    {
      id: "guardia",
      name: "Guardia Zoofila",
      fullName: "Guardie Zoofile Volontarie",
      phone: "800-253608",
      description: "Segnalazioni abbandono, maltrattamenti, animali pericolosi",
      color: "from-orange-500 to-orange-600",
      icon: Shield,
      tollFree: true
    },
    {
      id: "carabinieri",
      name: "Carabinieri",
      fullName: "Nucleo CITES",
      phone: "112",
      description: "Traffico animali, specie protette, emergenze gravi",
      color: "from-blue-600 to-blue-700",
      icon: Shield
    }
  ];

  // Desktop Layout
  if (!isMobile) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]" data-testid="emergency-page">
        <div className="max-w-5xl mx-auto p-8">
          {/* Header */}
          <header className="mb-8">
            <div className="flex items-center gap-4 mb-2">
              <button
                onClick={() => navigate(-1)}
                className="w-10 h-10 rounded-xl bg-white border border-black/5 flex items-center justify-center hover:bg-[#F5F5F7] transition-colors"
              >
                <ArrowLeft size={20} />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-[#1D1D1F]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Emergenza
                </h1>
                <p className="text-[#86868B]">Numeri utili e contatti di emergenza</p>
              </div>
            </div>
          </header>

          {/* Grid Layout */}
          <div className="grid grid-cols-3 gap-6">
            {/* Main Emergency Banner - Spans 2 cols */}
            <div className="col-span-2 bg-gradient-to-br from-red-500 to-red-600 rounded-3xl p-8 text-white relative overflow-hidden">
              <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full bg-white/10" />
              <div className="absolute -bottom-10 -left-10 w-40 h-40 rounded-full bg-white/10" />
              
              <div className="relative z-10 flex items-start gap-6">
                <div className="w-20 h-20 rounded-2xl bg-white/20 flex items-center justify-center">
                  <AlertTriangle size={40} />
                </div>
                <div>
                  <h2 className="text-2xl font-bold mb-2">Situazione di emergenza?</h2>
                  <p className="text-red-100 text-lg mb-4">
                    Mantieni la calma e chiama immediatamente uno dei numeri qui sotto
                  </p>
                  <div className="flex gap-3">
                    <a
                      href="tel:063242873"
                      className="px-6 py-3 bg-white text-red-600 rounded-xl font-bold hover:bg-red-50 transition-colors"
                    >
                      Chiama ENPA
                    </a>
                    <a
                      href="tel:112"
                      className="px-6 py-3 bg-white/20 text-white rounded-xl font-bold hover:bg-white/30 transition-colors"
                    >
                      112 Emergenze
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions - 1 col */}
            <div className="space-y-4">
              <Button
                onClick={() => navigate('/lost-pets')}
                variant="outline"
                className="w-full h-24 rounded-2xl border-amber-200 bg-amber-50 hover:bg-amber-100 flex flex-col items-center justify-center gap-2"
              >
                <AlertTriangle className="text-amber-600" size={28} />
                <span className="text-amber-700 font-semibold">Animale Smarrito</span>
              </Button>
              
              <Button
                onClick={() => navigate('/marketplace')}
                variant="outline"
                className="w-full h-24 rounded-2xl border-blue-200 bg-blue-50 hover:bg-blue-100 flex flex-col items-center justify-center gap-2"
              >
                <MapPin className="text-blue-600" size={28} />
                <span className="text-blue-700 font-semibold">Veterinario Vicino</span>
              </Button>
            </div>

            {/* Emergency Cards */}
            {emergencyCards.map((contact) => {
              const Icon = contact.icon;
              return (
                <a
                  key={contact.id}
                  href={`tel:${contact.phone.replace(/[^0-9]/g, '')}`}
                  className="bg-white rounded-2xl p-6 shadow-sm border border-black/5 hover:shadow-lg hover:scale-[1.02] transition-all"
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${contact.color} flex items-center justify-center flex-shrink-0`}>
                      <Icon className="text-white" size={28} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-bold text-[#1D1D1F]">{contact.name}</h3>
                        {contact.tollFree && (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                            Gratuito
                          </span>
                        )}
                        {contact.priority && (
                          <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                            Prioritario
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-[#86868B] mb-2">{contact.description}</p>
                      <p className="text-xl font-bold text-[#1D1D1F]">{contact.phone}</p>
                    </div>
                  </div>
                </a>
              );
            })}

            {/* Tips Section - Spans 2 cols */}
            <div className="col-span-2 bg-white rounded-2xl p-6 shadow-sm border border-black/5">
              <h3 className="font-bold text-[#1D1D1F] mb-4 flex items-center gap-2">
                <AlertTriangle className="text-amber-500" size={20} />
                Cosa fare in caso di emergenza
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <span className="w-8 h-8 rounded-full bg-red-100 text-red-600 flex items-center justify-center flex-shrink-0 font-bold">1</span>
                  <span className="text-[#6E6E73]">Mantieni la calma e metti in sicurezza l'animale</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="w-8 h-8 rounded-full bg-red-100 text-red-600 flex items-center justify-center flex-shrink-0 font-bold">2</span>
                  <span className="text-[#6E6E73]">Chiama immediatamente il veterinario o ENPA</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="w-8 h-8 rounded-full bg-red-100 text-red-600 flex items-center justify-center flex-shrink-0 font-bold">3</span>
                  <span className="text-[#6E6E73]">Non somministrare farmaci senza consulto medico</span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="w-8 h-8 rounded-full bg-red-100 text-red-600 flex items-center justify-center flex-shrink-0 font-bold">4</span>
                  <span className="text-[#6E6E73]">Porta con te il documento sanitario dell'animale</span>
                </div>
              </div>
            </div>

            {/* Local Emergency Numbers - 1 col */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-100">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-[#1D1D1F] flex items-center gap-2">
                  <Navigation className="text-blue-500" size={20} />
                  Nella tua zona
                </h3>
                {region && (
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                    {region}
                  </span>
                )}
              </div>
              
              {geoLoading ? (
                <div className="flex items-center gap-2 text-[#86868B]">
                  <Loader2 className="animate-spin" size={16} />
                  <span className="text-sm">Ricerca in corso...</span>
                </div>
              ) : (
                <div className="space-y-3">
                  {localVets.map((vet, i) => (
                    <a
                      key={i}
                      href={`tel:${vet.phone.replace(/[^0-9]/g, '')}`}
                      className="block p-3 bg-white rounded-xl hover:shadow-md transition-all"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-[#1D1D1F] text-sm">{vet.name}</p>
                          <p className="text-xs text-[#86868B] flex items-center gap-1">
                            <Clock size={12} />
                            {vet.hours}
                            {vet.tollFree && " • Gratuito"}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-blue-600">{vet.phone}</span>
                          <Phone size={16} className="text-blue-500" />
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              )}
            </div>
            <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl p-6 border border-red-100">
              <div className="flex items-center gap-3 mb-3">
                <Heart className="text-red-500" size={24} fill="currentColor" />
                <h3 className="font-bold text-[#1D1D1F]">ENPA Italia</h3>
              </div>
              <p className="text-sm text-[#6E6E73] mb-4">
                L'Ente Nazionale Protezione Animali è la più antica associazione animalista italiana, attiva dal 1871.
              </p>
              <a
                href="https://www.enpa.it"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-red-600 font-medium text-sm hover:underline"
              >
                Visita il sito ENPA
                <ExternalLink size={14} />
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Mobile Layout (original)
  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-28 overflow-x-hidden max-w-full" data-testid="emergency-page">
      {/* Header - Simplified */}
      <div className="bg-white border-b border-black/5 sticky top-0 z-40">
        <div className="px-4 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 rounded-xl bg-[#F5F5F7] flex items-center justify-center"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-[#1D1D1F]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Emergenza
            </h1>
            <p className="text-sm text-[#86868B]">Numeri utili e contatti</p>
          </div>
        </div>
      </div>

      <div className="px-4 py-4">
        {/* Emergency Banner */}
        <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-2xl p-4 text-white mb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
              <AlertTriangle size={24} />
            </div>
            <div>
              <p className="font-bold">Situazione di emergenza?</p>
              <p className="text-sm text-red-100">Chiama subito un numero qui sotto</p>
            </div>
          </div>
        </div>

        {/* Emergency Cards */}
        <div className="space-y-3 mb-6">
          {emergencyCards.map((contact) => {
            const Icon = contact.icon;
            return (
              <a
                key={contact.id}
                href={`tel:${contact.phone.replace(/[^0-9]/g, '')}`}
                className="block bg-white rounded-2xl p-4 shadow-sm border border-black/5 hover:shadow-md transition-all"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${contact.color} flex items-center justify-center flex-shrink-0`}>
                    <Icon className="text-white" size={20} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-slate-800 text-sm">{contact.name}</p>
                      {contact.tollFree && (
                        <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full">
                          Gratuito
                        </span>
                      )}
                      {contact.priority && (
                        <span className="text-[10px] bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full">
                          Prioritario
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 line-clamp-1">{contact.description}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="font-bold text-slate-800 text-sm">{contact.phone}</p>
                  </div>
                </div>
              </a>
            );
          })}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-2 mb-6">
          <Button
            onClick={() => navigate('/lost-pets')}
            variant="outline"
            className="h-auto py-3 rounded-2xl border-amber-200 bg-amber-50 hover:bg-amber-100 flex flex-col items-center gap-1.5"
          >
            <AlertTriangle className="text-amber-600" size={22} />
            <span className="text-amber-700 font-medium text-sm">Animale Smarrito</span>
          </Button>
          
          <Button
            onClick={() => navigate('/vets')}
            variant="outline"
            className="h-auto py-3 rounded-2xl border-blue-200 bg-blue-50 hover:bg-blue-100 flex flex-col items-center gap-1.5"
          >
            <MapPin className="text-blue-600" size={22} />
            <span className="text-blue-700 font-medium text-sm">Veterinario Vicino</span>
          </Button>
        </div>

        {/* Tips Section */}
        <div className="bg-slate-50 rounded-2xl p-4">
          <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2 text-sm">
            <AlertTriangle className="text-amber-500" size={18} />
            Cosa fare in caso di emergenza
          </h3>
          <ul className="space-y-2 text-sm text-slate-600">
            <li className="flex items-start gap-2">
              <span className="w-5 h-5 rounded-full bg-red-100 text-red-600 flex items-center justify-center flex-shrink-0 text-xs font-bold">1</span>
              <span>Mantieni la calma e metti in sicurezza l'animale</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-5 h-5 rounded-full bg-red-100 text-red-600 flex items-center justify-center flex-shrink-0 text-xs font-bold">2</span>
              <span>Chiama immediatamente il veterinario o ENPA</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-5 h-5 rounded-full bg-red-100 text-red-600 flex items-center justify-center flex-shrink-0 text-xs font-bold">3</span>
              <span>Non somministrare farmaci senza consulto medico</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-5 h-5 rounded-full bg-red-100 text-red-600 flex items-center justify-center flex-shrink-0 text-xs font-bold">4</span>
              <span>Porta con te il documento sanitario dell'animale</span>
            </li>
          </ul>
        </div>

        {/* ENPA Info */}
        <div className="mt-6 bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl p-5 border border-red-100">
          <div className="flex items-center gap-3 mb-3">
            <Heart className="text-red-500" size={24} fill="currentColor" />
            <h3 className="font-bold text-slate-800">ENPA Italia</h3>
          </div>
          <p className="text-sm text-slate-600 mb-4">
            L'Ente Nazionale Protezione Animali è la più antica associazione animalista italiana.
            Attiva dal 1871 per la tutela di tutti gli animali.
          </p>
          <a
            href="https://www.enpa.it"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-red-600 font-medium text-sm"
          >
            Visita il sito ENPA
            <ExternalLink size={14} />
          </a>
        </div>
      </div>
    </div>
  );
}
